import {BackendResponse} from '@common/http/backend-response/backend-response';
import {apiClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {useMutation} from '@tanstack/react-query';
import {
  getBootstrapData,
  mergeBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {Localization} from '@ui/i18n/localization';

interface ChangeLocaleResponse extends BackendResponse {
  locale: Localization;
}

export function useChangeLocale() {
  return useMutation({
    mutationFn: (props: {locale?: string}) =>
      apiClient
        .post<ChangeLocaleResponse>(`users/me/locale`, props)
        .then(r => r.data),
    onSuccess: response => {
      const rtlLocales = ['ar', 'fa', 'ur', 'he'];
      document.documentElement.dir = rtlLocales.includes(
        response.locale.language,
      )
        ? 'rtl'
        : 'ltr';
      document.documentElement.lang = response.locale.language;
      // Merge the new locale's translation `lines` back into the cached locales
      // list. Without this the components keep reading the OLD locale's strings
      // (the user-visible symptom: nav text doesn't change until a full page
      // reload). Update the matching locale in-place, or push a new one.
      const existing = getBootstrapData().i18n.locales || [];
      const updatedLocales = existing.some(
        l => l.language === response.locale.language,
      )
        ? existing.map(l =>
            l.language === response.locale.language
              ? {
                  ...l,
                  name: response.locale.name,
                  lines: response.locale.lines ?? l.lines,
                }
              : l,
          )
        : [
            ...existing,
            {
              name: response.locale.name,
              language: response.locale.language,
              lines: response.locale.lines ?? {},
            },
          ];
      mergeBootstrapData({
        i18n: {
          locales: updatedLocales,
          active: response.locale.language,
        },
      });
    },
    onError: err => showHttpErrorToast(err),
  });
}
